import { api } from '../api';
import type {
  CourseDetailDto,
  CourseListItemDto,
  LessonDto,
  LessonSummaryDto,
  MentorTeam,
  MyGamificationDto,
  QuizForAttemptDto,
} from '../wikiApi';

/**
 * API волны W3 — обучение: каталог, курс, урок, тест, траектория, сертификат,
 * очки, наставник (+ телефон ученика): continueLessonId в списке курсов,
 * главы видео, практическое задание, необязательные уроки/правило открытия.
 *
 * Правила (план wk6 §4): lib/wikiApi.ts заморожен после фазы 0 — новые вызовы
 * и аддитивные DTO волна кладёт только в свой модуль. Клиент — `api` из
 * '../api'. Существующие DTO расширять пересечением:
 *   type CourseDetailX = CourseDetailDto & { authorName?: string };
 *
 * Здесь же — ОБЩИЙ контракт сдачи практики (W3 ↔ W5):
 * W3 — сторона ученика (сдать, увидеть статус/возврат), W5 — проверка в
 * «Проверке ответов» (accept → урок зачтён + очки; return → комментарий +
 * уведомление). W5 импортирует типы отсюда, не дублирует.
 * Таблица — wiki_practical_submissions (миграция 202610170010_wiki_hub2).
 */

/** pending — ждёт проверки; accepted — зачтено; returned — вернули на доработку. */
export type PracticalSubmissionStatus = 'pending' | 'accepted' | 'returned';

/** Сдача практического задания урока type=practical. */
export interface PracticalSubmission {
  id: string;
  lessonId: string;
  /** Для списков проверки (W5) и истории ученика (W3). */
  lessonTitle?: string;
  courseId?: string | null;
  courseName?: string | null;
  /** crm:<users.id> | emp:<employee_accounts.id>. */
  ownerKey: string;
  /** ФИО ученика — заполняется в списке проверяющего. */
  ownerName?: string | null;
  /** Точка ученика (formatPointName на бэке) — для списка проверяющего. */
  pointName?: string | null;
  /** wiki_attachments.id фото/файла (может не быть — только комментарий). */
  attachmentId: string | null;
  /** Ссылка на скачивание вложения (с ACL на бэке, setSafeFileHeaders). */
  attachmentUrl: string | null;
  /** MIME вложения — превью картинки vs ссылка на файл. */
  attachmentMime?: string | null;
  /** Комментарий ученика. */
  comment: string | null;
  status: PracticalSubmissionStatus;
  /** Оценка проверяющего (0–100), если задана. */
  score: number | null;
  /** Комментарий проверяющего (обязателен при returned). */
  reviewerComment: string | null;
  reviewedByName?: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Сдать практику (W3): POST /wiki/education/lessons/:id/practical.
 * Файл грузится отдельно (multipart) → attachmentId; повторная сдача после
 * returned создаёт новую строку (история попыток сохраняется).
 */
export interface PracticalSubmitInput {
  attachmentId?: string | null;
  comment?: string | null;
}

/** Решение проверяющего (W5), в общем apply «Проверки ответов». */
export interface PracticalReviewInput {
  decision: 'accept' | 'return';
  score?: number | null;
  /** Обязателен для return. */
  comment?: string | null;
}

/**
 * Конфиг практики в `trainer` JSON урока type=practical (заполняет
 * конструктор W4, читает бэк parsePracticalConfig): задание, критерии
 * приёмки, кто проверяет, обязательно ли фото. review=false — без проверки
 * (урок зачитывается кнопкой «Выполнил», как старый текстовый). Если задано
 * criteria / requirePhoto / reviewer — проверка обязательна (review по
 * умолчанию true), урок зачтёт только «Принять» в «Проверке ответов».
 */
export interface PracticalConfigInput {
  task?: string;
  criteria?: string[];
  reviewer?: 'manager' | 'uk';
  requirePhoto?: boolean;
  review?: boolean;
}

/** Конфиг практики после нормализации на бэке (в уроке). */
export interface PracticalConfig {
  task: string | null;
  criteria: string[];
  reviewer: 'manager' | 'uk' | null;
  requirePhoto: boolean;
  reviewRequired: boolean;
}

export type UnlockRule = 'sequential' | 'free' | 'after_prev_passed';

/** Глава видео-урока (wiki_lessons.chapters): t — секунда начала. */
export interface VideoChapter {
  t: number;
  title: string;
}

// ── Аддитивные поля W3 к DTO фазы 2 ─────────────────────────────────

/** Курс в каталоге + «Продолжить» (первый несданный обязательный урок). */
export type LearnCourseListItem = CourseListItemDto & {
  continueLessonId?: string | null;
  continueLessonTitle?: string | null;
  continueLessonType?: string | null;
  continueLessonIndex?: number | null;
  continueLessonMinutes?: number | null;
  updatedAt?: string;
};

export type LearnLessonSummary = LessonSummaryDto & {
  isOptional?: boolean;
  unlockRule?: UnlockRule;
  isLocked?: boolean;
  lockHard?: boolean;
  bestScore?: number | null;
  passingScore?: number | null;
  points?: number | null;
};

export type LearnCourseDetail = Omit<CourseDetailDto, 'modules'> & {
  requiredLessons?: number;
  completedAt?: string | null;
  authorName?: string | null;
  updatedAt?: string;
  learnersCount?: number;
  passingScore?: number | null;
  recertifyEveryDays?: number | null;
  modules: Array<Omit<CourseDetailDto['modules'][number], 'lessons'> & { lessons: LearnLessonSummary[] }>;
};

export type LearnLesson = Omit<LessonDto, 'toc'> & {
  chapters?: VideoChapter[];
  isOptional?: boolean;
  unlockRule?: UnlockRule;
  lock?: { hard: boolean; prevLessonId: string | null; prevLessonTitle: string | null } | null;
  index?: number | null;
  rewards?: { lesson: number; quizPass: number; quizFirstTry: number };
  practical?: (PracticalConfig & { submission: PracticalSubmission | null; attempts: number }) | null;
  toc: Array<Omit<LessonDto['toc'][number], 'lessons'> & {
    lessons: Array<LessonDto['toc'][number]['lessons'][number] & { isOptional?: boolean; locked?: boolean }>;
  }>;
};

/** Тест перед стартом + правила (HUB 2.0: перемешивание, очки, ручная проверка). */
export type LearnQuiz = Omit<QuizForAttemptDto, 'policy'> & {
  policy: QuizForAttemptDto['policy'] & { shuffleQuestions?: boolean; shuffleOptions?: boolean };
  points?: { pass: number; firstTry: number };
  hasManualReview?: boolean;
};

/** Наставник: участник команды + активная траектория новичка. */
export type LearnMentorTeam = Omit<MentorTeam, 'members'> & {
  members: Array<MentorTeam['members'][number] & {
    path?: { name: string; assignedAt: string; dueAt: string | null; doneSteps: number | null; totalSteps: number } | null;
  }>;
};

async function uploadForm<T>(path: string, fd: FormData): Promise<T> {
  const r = await fetch(`/api${path}`, { method: 'POST', body: fd, credentials: 'include' });
  if (!r.ok) {
    let msg = `Ошибка отправки (${r.status})`;
    try {
      const j = (await r.json()) as { message?: string | string[] };
      if (j.message) msg = Array.isArray(j.message) ? j.message.join(', ') : j.message;
    } catch {
      /* не JSON */
    }
    throw new Error(msg);
  }
  return (await r.json()) as T;
}

/** Мой сертификат (GET /wiki/education/certificates/mine) — для профиля (W5) и очков. */
export interface MyCertificate {
  courseId: string;
  courseName: string;
  number: string;
  issuedAt: string;
  expiresAt: string | null;
  isExpired: boolean;
}

/** Вызовы волны W3 (ученик). */
export const learnApi = {
  /** Русские названия ролей (CRM + должности iiko): код → название. Доступно любому вошедшему в вики. */
  roleLabels: () => api.get<{ labels: Record<string, string> }>('/wiki/education/role-labels'),
  listCourses: () => api.get<LearnCourseListItem[]>('/wiki/education/courses'),
  getCourse: (id: string) => api.get<LearnCourseDetail>(`/wiki/education/courses/${encodeURIComponent(id)}`),
  getLesson: (id: string) => api.get<LearnLesson>(`/wiki/education/lessons/${encodeURIComponent(id)}`),
  getQuiz: (id: string) => api.get<LearnQuiz>(`/wiki/education/quizzes/${encodeURIComponent(id)}`),
  /** Очки с историей начислений (страница «Мои очки»: до 50). */
  gamification: (history = 30) => api.get<MyGamificationDto | null>(`/wiki/education/gamification/mine?history=${history}`),
  mentorTeam: (pointId?: string | null) =>
    api.get<LearnMentorTeam>(`/wiki/education/mentor/team${pointId ? `?pointId=${encodeURIComponent(pointId)}` : ''}`),
  myCertificates: () => api.get<MyCertificate[]>('/wiki/education/certificates/mine'),
  /** Мои сдачи практики по уроку (новые сверху). */
  myPractical: (lessonId: string) => api.get<PracticalSubmission[]>(`/wiki/education/lessons/${encodeURIComponent(lessonId)}/practical`),
  /**
   * Сдать практику: фото (камера телефона) и/или комментарий. Без нового
   * файла можно переотправить фото прошлой своей сдачи (attachmentId).
   */
  submitPractical: (lessonId: string, input: PracticalSubmitInput & { file?: File | null }) => {
    const path = `/wiki/education/lessons/${encodeURIComponent(lessonId)}/practical`;
    if (input.file) {
      const fd = new FormData();
      fd.append('file', input.file);
      if (input.comment) fd.append('comment', input.comment);
      return uploadForm<PracticalSubmission>(path, fd);
    }
    return api.post<PracticalSubmission>(path, { comment: input.comment || undefined, attachmentId: input.attachmentId || undefined });
  },
};
